import React, { forwardRef } from "react";
import { cn } from "@/lib/utils";
import type { ArchNode } from "@/data/architecture";

export type NodeState = "idle" | "focused" | "dimmed";

interface ProtocolMapNodeProps {
  node: ArchNode;
  state: NodeState;
  /** Width and height in viewBox units (the SVG underlay shares these). */
  width: number;
  height: number;
  /** Centre coordinates in viewBox units. */
  cx: number;
  cy: number;
  /** Conversion factor from viewBox units to overlay container percentage. */
  viewBoxWidth: number;
  viewBoxHeight: number;
  interactive: boolean;
  onHover?: (id: string | null) => void;
  onSelect?: (id: string) => void;
}

/**
 * A single node in the protocol map.
 *
 * Rendered as a real DOM <button> positioned absolutely inside a container
 * whose aspect-ratio matches the SVG viewBox. We translate viewBox
 * coordinates to percentage offsets so the overlay scales with the SVG.
 */
export const ProtocolMapNode = forwardRef<HTMLButtonElement, ProtocolMapNodeProps>(
  function ProtocolMapNode(
    {
      node,
      state,
      width,
      height,
      cx,
      cy,
      viewBoxWidth,
      viewBoxHeight,
      interactive,
      onHover,
      onSelect,
    },
    ref,
  ) {
    const leftPct = ((cx - width / 2) / viewBoxWidth) * 100;
    const topPct = ((cy - height / 2) / viewBoxHeight) * 100;
    const widthPct = (width / viewBoxWidth) * 100;
    const heightPct = (height / viewBoxHeight) * 100;

    const isFocused = state === "focused";
    const isDimmed = state === "dimmed";

    return (
      <button
        ref={ref}
        type="button"
        data-node-id={node.id}
        aria-label={`${node.label}. ${node.tooltip}`}
        aria-pressed={isFocused}
        tabIndex={interactive ? 0 : -1}
        onMouseEnter={interactive ? () => onHover?.(node.id) : undefined}
        onMouseLeave={interactive ? () => onHover?.(null) : undefined}
        onFocus={interactive ? () => onHover?.(node.id) : undefined}
        onBlur={interactive ? () => onHover?.(null) : undefined}
        onClick={interactive ? () => onSelect?.(node.id) : undefined}
        className={cn(
          "absolute flex flex-col justify-center items-start text-left",
          "px-3 py-2 border bg-paper",
          "transition-[opacity,border-color,background-color,box-shadow] duration-300",
          "focus:outline-none focus:ring-1 focus:ring-ink",
          interactive ? "cursor-pointer" : "cursor-default",
          // base border + states
          isFocused
            ? "border-ink shadow-[0_2px_0_0_rgba(17,17,17,0.06)] bg-paper-2"
            : "border-hairline",
          interactive && !isFocused && "hover:border-ink-2 hover:bg-paper-2",
          isDimmed && "opacity-25",
        )}
        style={{
          left: `${leftPct}%`,
          top: `${topPct}%`,
          width: `${widthPct}%`,
          height: `${heightPct}%`,
        }}
      >
        <div className="flex items-center gap-1.5 w-full min-w-0">
          <span className="font-mono text-[11px] md:text-[12px] text-ink truncate leading-tight">
            {node.label}
          </span>
          {node.planned && (
            <span className="ml-auto shrink-0 font-mono text-[8px] uppercase tracking-widest text-state-pending border border-state-pending/50 px-1 py-px leading-none">
              planned
            </span>
          )}
        </div>
        {node.caption && (
          <span className="font-serif italic text-[10px] md:text-[11px] text-ink-2 truncate leading-tight mt-0.5 w-full">
            {node.caption}
          </span>
        )}
      </button>
    );
  },
);
